import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchInput } from "@/components/common/SearchInput";
import { Award, Settings } from "lucide-react";

const BAND_FAMILY = {
  "Outstanding Performer": "green", "Strong Performer": "blue", "Average Performer": "amber",
  "Needs Improvement": "amber", "Unsatisfactory": "red",
};

export default function Performance() {
  const navigate = useNavigate();
  const { me, can } = useApp();
  const [cycleId, setCycleId] = useState(null);
  const [search, setSearch] = useState("");
  const [bandFilter, setBandFilter] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all");
  const { data: cycles = [] } = useQuery({ queryKey: ["perf-cycles"], queryFn: () => get("/hrms/performance/cycles") });
  const activeCycle = cycleId || cycles[0]?.id;
  const { data: summary, isLoading } = useQuery({
    queryKey: ["perf-summary", activeCycle], queryFn: () => get(`/hrms/performance/summary?cycle_id=${activeCycle}`),
    enabled: !!activeCycle,
  });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: workload = [] } = useQuery({ queryKey: ["pm-workload"], queryFn: () => get("/pm/workload") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const wmap = useMemo(() => Object.fromEntries(workload.map((w) => [w.user_id, w])), [workload]);
  const cycle = cycles.find((c) => c.id === activeCycle);
  const filtered = useMemo(() => {
    return (summary || []).filter((r) => {
      if (search && !r.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (bandFilter !== "all" && r.band !== bandFilter) return false;
      if (approvalFilter !== "all") {
        const isApproved = !!r.approved;
        if (approvalFilter === "approved" && !isApproved) return false;
        if (approvalFilter === "pending" && isApproved) return false;
      }
      return true;
    });
  }, [summary, search, bandFilter, approvalFilter]);

  return (
    <div>
      <PageHeader title="Performance" subtitle={cycle ? `${cycle.name} · ${cycle.period}` : "Review cycles and scorecards."}>
        {cycles.length > 0 && (
          <Select value={activeCycle} onValueChange={setCycleId}>
            <SelectTrigger className="w-56" data-testid="cycle-select"><SelectValue /></SelectTrigger>
            <SelectContent>{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.status})</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="outline" data-testid="recognition-link" onClick={() => navigate("/hrms/recognition")}>
          <Award className="mr-1.5 h-4 w-4" />Recognition
        </Button>
        {can("hrms", "edit") && (
          <Button variant="outline" data-testid="cycles-link" onClick={() => navigate("/hrms/performance/cycles")}>
            <Settings className="mr-1.5 h-4 w-4" />Manage Cycles
          </Button>
        )}
        {cycle && (
          <Button data-testid="my-review-btn" onClick={() => navigate(`/hrms/performance/review/${cycle.id}/${me?.id}/self`)}>
            Submit My Self-Review
          </Button>
        )}
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : !summary?.length ? (
        <EmptyState icon={Award} title="No submitted reviews yet" description="Scorecards will appear here once self and manager reviews are submitted." />
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <SearchInput testid="perf-search" placeholder="Search employee" value={search} onChange={setSearch} />
            <Select value={bandFilter} onValueChange={setBandFilter}>
              <SelectTrigger className="w-48" data-testid="perf-band-filter"><SelectValue placeholder="All bands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bands</SelectItem>
                {Object.keys(BAND_FAMILY).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={approvalFilter} onValueChange={setApprovalFilter}>
              <SelectTrigger className="w-44" data-testid="perf-approval-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={Award} title="No matching reviews" description="Adjust your search or filters." />
          ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Employee</th><th className="px-4 py-3 font-semibold">Self</th>
              <th className="px-4 py-3 font-semibold">Manager</th><th className="px-4 py-3 font-semibold">HR</th>
              <th className="px-4 py-3 font-semibold">Combined</th><th className="px-4 py-3 font-semibold">Band</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">PM Delivery</th></tr></thead>
            <tbody>
              {filtered.map((r) => {
                const w = wmap[r.employee_id];
                return (
                  <tr key={r.employee_id} data-testid={`perf-row-${r.employee_id}`} onClick={() => navigate(`/hrms/performance/${activeCycle}/${r.employee_id}`)}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar user={umap[r.employee_id]} size="sm" /><span className="font-medium">{r.name}</span></div></td>
                    <td className="px-4 py-3">{r.self_pct != null ? `${r.self_pct}%` : "—"}</td>
                    <td className="px-4 py-3">{r.manager_pct != null ? `${r.manager_pct}%` : "—"}</td>
                    <td className="px-4 py-3">{r.hr_pct != null ? `${r.hr_pct}%` : "—"}</td>
                    <td className="px-4 py-3 font-medium">{r.combined_pct}%</td>
                    <td className="px-4 py-3"><StatusBadge family={BAND_FAMILY[r.band]} label={r.band} /></td>
                    <td className="px-4 py-3">{r.approved ? <StatusBadge family="green" label="Approved" /> : <StatusBadge family="slate" label="Pending Approval" />}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{w ? `${w.total_items} open · ${w.overdue} overdue` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}
    </div>
  );
}
